<template>
  <MetaInfo :title="meta.title" :description="meta.description"></MetaInfo>

  <div>
    <navigation></navigation>

    <PageHeader title="EU Data Act Addendum"> </PageHeader>

    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-12 col-md-3 mb-20">
          <TermsNav></TermsNav>
        </div>
        <div class="col-xs-12 col-md-9">
          <div class="longform">
            <h3>1. Applicability</h3>
            <p>
              1.1. This EU Data Act Addendum ("DAA") sets out the specific rights and obligations 
              of the Customer and the Company when the Customer wishes to exercise its rights under 
              the EU Data Act to switch to another provider or to an on-premise ICT infrastructure. 
              This DAA applies solely to the provision of Service to Customers located in the European Union,
               or to whom the EU Data Act is otherwise applicable.
            </p>
            <p>
              1.2. The terms of this DAA are incorporated into and form part of the Agreement.
            </p>
            <p>
              1.3. This DAA does not apply to: (i) any bespoke or custom services not generally 
              available to all customers; or (ii) any non-production, testing or BETA or free versions 
              of the Service.
            </p>
            <h3>2. Definitions</h3>
            <p>
              All terms in capitals used in this DAA shall have the meaning set out in the Agreement 
              unless defined otherwise in this DAA.
            </p>
            <div class="terms-table terms-table--2-col">
              <table>
                <tbody>
                  <tr>
                    <th valign="top">"Agreement"</th>
                    <td valign="top">
                      means the written agreement between the parties for the provision of the Service by the Company;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Company"</th>
                    <td valign="top">
                      means the supplier of the Service named in the Agreement;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Customer"</th>
                    <td valign="top">
                      means the customer named in the Agreement;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Data Processing Services"</th>
                    <td valign="top">
                      means digital services that are provided to a Customer and that 
                      enable ubiquitous and on-demand network access to a shared pool 
                      of configurable, scalable and elastic computing resources of a 
                      centralised, distributed or highly distributed nature that can be 
                      rapidly provisioned and released with minimal management effort or 
                      service provider interaction;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Destination Provider"</th>
                    <td valign="top">
                      means the destination provider of Data Processing Services,
                      whereby the Customer changes from using the Data Processing Services
                      of the Company to using other Data Processing Services of the same service
                      type, or other service, offered by such a different provider of Data
                      Processing Services, or to an on-premises ICT infrastructure including
                      through extracting, transforming and uploading the data,
                      as set out in Article 2(34) of the EU Data Act;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Digital Assets"</th>
                    <td valign="top">
                      means elements in digital form, including applications, for which the Customer
                      has the right of use, independently from the contractual relationship with the
                      Data Processing Services it intends to switch from, as set out in Article 2(32)
                      of the EU Data Act;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"EU Data Act"</th>
                    <td valign="top">
                      means Regulation (EU) 2023/2854 of the European Parliament and Council 
                      of 13 December 2023 on harmonised rules on fair access to and use of 
                      data and amending Regulation (EU) 2020/1828 (Data Act);
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Exportable Data"</th>
                    <td valign="top">
                      means the input and output data, including Metadata directly 
                      or indirectly generated or cogenerated, by Customer's use of 
                      the Data Processing Services, as set out in Article 2(38) of the EU Data Act, 
                      excluding any assets or data protected by intellectual property rights or 
                      constituting a trade secret of the Company or third parties, for example: usage data, 
                      source code and algorithms;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Fees"</th>
                    <td valign="top">
                      means all fees payable by the Customer to the Company for the provision 
                      of the Service, set out in the Agreement;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Initial Term"</th>
                    <td valign="top">
                      means an initial subscription period of 12 months where Fees are paid annually;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Metadata"</th>
                    <td valign="top">
                      means a structured description of Customer data or the use of data facilitating 
                      the discovery or use of Customer data, excluding usage data;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Renewal Term"</th>
                    <td valign="top">
                      means each renewal period following expiry of the Initial Term, which shall be 12 
                      months where Fees are paid annually;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Services"</th>
                    <td valign="top">
                      means the cloud services of the Company provided to the Customer pursuant to the Agreement, 
                      which are a data processing service, as set out in Art. 2(8) of the EU Data Act;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Switching Charges"</th>
                    <td valign="top">
                      means charges, other than the Fees or Early Termination Fees, imposed by the Company
                      on the Customer for switching to a Destination Provider. Switching Charges include, 
                      but are not limited to, costs related to the transit of data from one provider of 
                      Data Processing Services to another or to an on-premises ICT infrastructure 
                      (data egress charges) or the costs incurred for specific support actions during 
                      the Switching Process;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Switching Process"</th>
                    <td valign="top">
                      means the process involving the Company, the Customer and, where relevant, 
                      a Destination Provider, when the Customer changes from using the Service 
                      provided by the Company to using the services of a Destination Provider 
                      or to its own on-premises ICT infrastructure, including extracting, 
                      transforming and uploading Exportable Data and, where applicable, 
                      Digital Assets, as set out in Article 2(34) of the EU Data Act.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h3>3. Conflicts</h3>
            <p>
              In the event of any conflict between the terms of this DAA and the terms of the Agreement, 
              the terms of this DAA will prevail solely with respect to the subject matter herein.

            </p>

            <h3>4. Switching</h3>
            <p>
              4.1. To initiate the Switching Process the Customer must give the Company written 
              notice of its intention to: (i) switch to a different Destination Provider; 
              or (ii) erase its Exportable Data, and where applicable Digital Assets; 
              (each a <strong>"Switching Notice"</strong>). 
            </p>
            <p>
              4.2. The Customer must specify in the Switching Notice the services, 
              Exportable Data or Digital Assets that it wishes to switch: 
              and (i) where the Customer intends to switch to a new Destination 
              Provider the necessary details of the Destination Provider or whether 
              the Customer wishes to switch to its own on-premises ICT infrastructure; 
              or (ii) whether the Customer does not wish to switch but only to erase its 
              Exportable Data and Digital Assets.
            </p>
            <p>
              4.3. If the Customer wishes to switch to a different Destination Provider, 
              the Customer shall take reasonable measures to achieve effective switching. 
              The Customer will be responsible for the import, implementation, and configuration 
              of Exportable Data and Digital Assets in its own systems or in the systems of the 
              Destination Provider.
            </p>
            <p>
              4.4. Any concerns or disputes relating to the Switching Process shall be 
              addressed in good faith discussions between the parties and, if unresolved, 
              escalated pursuant to the dispute resolution procedures set out in the Agreement.
            </p>
            <p>
              4.5. A written Switching Notice must be sent to the Company no later than 2 months 
              prior to the start of the Switching Process, <strong>("Notice Period")</strong>. The Customer shall 
              continue to pay all Fees in full in accordance with the Agreement during the Notice Period.
            </p>
            <p>
              4.6. The Company shall use reasonable efforts to facilitate completion of the Switching 
              Process within 30 days of the end of the Notice Period, <strong>("Transitional Period")</strong>.
            </p>

            <h3>5. Transitional Period</h3>
            <p>
              5.1. The Company shall provide reasonable support to the Customer and third parties authorised 
              by the Customer to switch to the Destination Provider during the Transitional Period.
            </p>
            <p>
              5.2. If technical complexity necessitates a longer Transitional Period, the Company 
              shall notify the Customer within 14 calendar days of receiving the Switching Notice. 
              The Company will duly justify the technical or other obstacles that prevent switching 
              within the Transitional Period and indicate an alternative Transitional Period, 
              which shall not exceed 7 months following the Notice Period, <strong>("Company Extension Notice")</strong>.
            </p>
            <p>5.3. The Customer may request one extension to the Transitional Period for a reasonable period, 
              by giving the Company written notice, not later than 1 month before the Transitional Period would 
              otherwise expire stating the alternative Transitional Period requested, <strong>("Customer Extension Notice")</strong>. 
              The Company shall consider the request and use reasonable efforts to comply with the Customer's request.
            </p>
            <p>
              5.4. The Customer shall continue to pay all Fees in full in accordance with the Agreement during any Transitional Period.
            </p>

            <h3>6. Data Portability and Deletion</h3>
            <p>
              6.1. The Company shall retain Exportable Data and Digital Assets for a period of 30 days 
              after the Transitional Period expires, <strong>("Retrieval Period")</strong>. The Customer may export or 
              delete Exportable Data or Digital Assets during the Retrieval Period. 
              The Customer is not entitled to receive: (i) usage data; (ii) source code; (iii) algorithms; 
              or (iv) any assets or data protected by intellectual property rights or constituting a trade 
              secret of the Company or any third parties.
            </p>
            <p>
              6.2. At the end of the Retrieval Period, the Company shall delete all Exportable Data and Digital 
              Assets, except for any Exportable Data or Digital Assets that the Company is required to retain 
              under applicable laws.
            </p>
            <p>
              6.3. The Customer shall continue to pay all Fees in full in accordance with the Agreement during 
              any Retrieval Period.
            </p>
            <p>
              6.4. The Customer is solely responsible for the import and implementation of Exportable Data or 
              Digital Assets into their new systems.
            </p>

            <h3>7. Customer Obligations</h3>
            <p>
              7.1. Throughout the Switching Process, the Customer shall: (i) to the extent 
              technically possible, self-export Exportable Data or Digital Assets directly 
              from the Service; (ii) import and implement Exportable Data and Digital Assets 
              to the Customer's on-premises ICT infrastructure or the systems of the Destination 
              Provider; and (ii) comply with the Customer's confidentiality obligations under the Agreement.
            </p>
            <p>
              7.2. The Customer shall promptly notify the Company in writing once the Switching 
              Process has been completed. If the Company is not notified of such completion, 
              the Company will assume the Switching Process is completed at the end of the 
              Retrieval Period.
            </p>
            <p>
              7.3. The Customer or third parties authorised by them, including the Destination Provider 
              undertake to respect the intellectual property rights and confidentiality of any materials 
              provided in the Switching Process by the Company, as well as the Company's trade secrets. 
              Any disclosure of the Company's confidential information to a third party 
              (including any Destination Provider) is subject to the Company's prior written approval 
              and shall be limited to what is necessary to complete the Switching Process until the end 
              of the agreed Transitional Period, including any extension thereto.
            </p>
            <p>
              7.4. If the Customer authorises a third party to manage or perform all or any part of the 
              Switching Process on its behalf the Customer must: (i) grant such third party the 
              necessary permissions and access rights on its account with the Company; 
              and (ii) if requested by the Company, provide any information that the 
              Company may reasonably request evidencing the appointment of such third 
              party and their acceptance to be bound by the Customer's obligations under 
              this DAA (and, where relevant, the Agreement) to the same extent as if those obligations 
              were their own.
            </p>

            <h3>8. Company Obligations</h3>
            <p>
              8.1. Throughout the Switching Process, the Company shall: (i) provide clear 
              information concerning known risks to continuity in the provision of the 
              Service which is attributable to the Company; (ii) act with due care to 
              maintain business continuity; (iii) continue the provision of the Service 
              under the Agreement; and (iv) ensure an appropriate level of security is 
              maintained throughout the Switching Process, in particular the security 
              of the Exportable Data and Digital Assets during their transfer and the 
              continued security of the Exportable Data and Digital Assets during the 
              Retrieval Period, in accordance with applicable law.
            </p>
            <p>
              8.2. The Company shall support the Customer's exit strategy related to the Service 
              by ensuring relevant information related to the Switching Process is made available 
              at: <a href="/support/account-and-user-management/exporting-your-data-from-transpond">https://transpond.io/support/account-and-user-management/exporting-your-data-from-transpond</a>, 
              which shall include an exhaustive list of all categories of Exportable Data and 
              Digital Assets that can be exported during the Switching Process.
            </p>
            <p>
              8.3. Where the Exportable Data and Digital Assets cannot be exported from the Service 
              by the Customer, the Company will provide the Customer with a switching and exit plan, 
              <strong>("Plan")</strong> that includes the porting methods and formats and steps required to carry out 
              the Switching Process. The Company will update the Plan when necessary and check, at 
              the Customer's request, if changes to the Plan are required.
            </p>
            
            <h3>Switching Charges</h3>
            <p>
              9.1. Switching Charges will be charged for the provision of Switching assistance up until 
              the 12th of January 2027 on a time and materials basis at the prevailing rate the 
              Company charges for professional services at the time the Switching Notice is received, 
              where it is impossible for the Customer to switch away from the Service provided by the 
              Company without needing the Company to provide assistance.
            </p>
            <p>
              9.2. Switching Charges are payable upon receipt of each invoice, unless agreed otherwise 
              in writing between the parties.
            </p>

            <h3>10. Termination of the Agreement</h3>
            <p>
              10.1. In addition to any other termination provisions set out in the Agreement, 
              the parties agree and acknowledge that the Agreement may be terminated by the Customer 
              with effect from: (i) the day after the Customer notifies the Company of the completion 
              of the Switching Process; or (ii) the end of the Retrieval Period; or (iii) the end of 
              the Notice Period if the Customer has requested the Company to erase the Exportable Data 
              and, where applicable, Digital Assets.
            </p>
            <p>
              10.2. Upon written request of the Customer, the Company will confirm termination of 
              the Agreement to the Customer, within 10 days of receipt of such a request.
            </p>

            <h3>11. Early Termination Fees</h3>
            <p>
              11.1. The Customer acknowledges pricing for the Service with fixed annual or multi-year 
              terms and minimum spend commitments is generally lower than for the Service purchased 
              monthly without such commitments. The Customer further acknowledges that, for any 
              Service subject to fixed annual or multi-year terms, the Customer had the option to 
              purchase those Services on a monthly renewing basis. Accordingly, if the Customer 
              exercises their rights under this DAA, to terminate the Agreement early, before expiry 
              of any fixed Initial Term or fixed Renewal Term, the Customer must pay an early termination 
              fee equal to the amount of the Fees that the Customer would have been obliged to pay to the 
              Company for the entire fixed Term of the Agreement, if the Agreement had not been terminated 
              early by the Customer under this DAA, <strong>("Early Termination Fee")</strong>.
            </p>
            <p>
              11.2. For Fees tied to usage, the Early Termination Fee shall include the value of 
              any minimum usage commitments for the Service from the effective date of termination 
              set out in clause 10.1(i), (ii) or (iii), as applicable, until the conclusion of the 
              Initial Term or Renewal Term, as applicable, regardless of whether the effective date 
              of termination occurs before the end of either the Initial Term or the Renewal Term. 
            </p>
            <p>
              11.3. The Customer shall receive no refunds of unused prepaid Fees. 
            </p>
            <p>
              11.4. The Customer shall not be entitled to any discounts for any Fees corresponding 
              to the Service where the Agreement is terminated early. Any discounts already applied 
              to past or future Fees shall no longer apply and the Early Termination Fee will be the 
              full non-discounted sum payable for the original fixed term of the Agreement.
            </p>
            <p>
              11.5. All Early Termination Fees are immediately due and payable upon termination of the Agreement. 
            </p>

            <h3>Notices</h3>
            <p>
              All notices under this DAA are to be sent by email as follows: (i) to the Customer’s email address for the administrator of the Customer account or, if no administrator is designated, to the email address used for receipt of invoices by the Customer. The Customer is responsible for updating its information with the Company, including providing the Company with an up to date email address for the provision of notices under the Agreement; and (ii) to the Company to: support@transpond.io.
            </p>

            <h3>Applicable Law and Courts</h3>
            <p>
              This DAA shall be governed by and construed in accordance with the same law and courts as set out in the Agreement.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
  <site-footer></site-footer>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import SiteFooter from "@/components/SiteFooter";
import TermsNav from "@/components/Terms/TermsNav";
import PageHeader from "@/components/Header/PageHeader";

export default {
  name: "TermsDAA",
  components: {
    MetaInfo,
    Navigation,
    SiteFooter,
    TermsNav,
    PageHeader,
  },

  data() {
    return {
      meta: {
        title: "EU Data Act Addendum - Transpond",
        description: "Take a look at the Transpond EU Data Act Addendum",
      },
    };
  },
};
</script>
